import React, { useState, useMemo } from 'react';
import { cn } from '../../lib/utils';
import { useBranding } from '../../contexts/BrandingContext';

const Field = ({ el, value, onChange, locale = 'en' }) => {
  const common = {
    className: 'w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring',
    value: value ?? '',
    onChange: e => onChange(e.target.value)
  };
  
  const label = typeof el.label === 'object' ? (el.label[locale] || el.label.en || el.key) : (el.label || el.key);
  const placeholder = typeof el.placeholder === 'object' ? (el.placeholder?.[locale] || el.placeholder?.en || '') : (el.placeholder || '');
  const helpText = typeof el.helpText === 'object' ? (el.helpText?.[locale] || el.helpText?.en) : el.helpText;
  
  if (el.type === 'short_text' || el.type === 'shortText') {
    return (
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">
          {label} {el.required && <span className="text-red-600">*</span>}
        </label>
        <input type="text" {...common} placeholder={placeholder} />
        {helpText && <div className="text-xs text-muted-foreground mt-1">{helpText}</div>}
      </div>
    );
  }
  if (el.type === 'long_text' || el.type === 'longText') {
    return (
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">
          {label} {el.required && <span className="text-red-600">*</span>}
        </label>
        <textarea rows={4} {...common} placeholder={placeholder} />
        {helpText && <div className="text-xs text-muted-foreground mt-1">{helpText}</div>}
      </div>
    );
  }
  if (el.type === 'email') {
    return (
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">
          {label} {el.required && <span className="text-red-600">*</span>}
        </label>
        <input type="email" {...common} placeholder={placeholder} />
        {helpText && <div className="text-xs text-muted-foreground mt-1">{helpText}</div>}
      </div>
    );
  }
  if (el.type === 'phone') {
    return (
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">
          {label} {el.required && <span className="text-red-600">*</span>}
        </label>
        <input type="tel" {...common} placeholder={placeholder} />
        {helpText && <div className="text-xs text-muted-foreground mt-1">{helpText}</div>}
      </div>
    );
  }
  if (el.type === 'number') {
    return (
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">
          {label} {el.required && <span className="text-red-600">*</span>}
        </label>
        <input type="number" step="1" {...common} placeholder={placeholder} />
        {helpText && <div className="text-xs text-muted-foreground mt-1">{helpText}</div>}
      </div>
    );
  }
  if (el.type === 'currency') {
    return (
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">
          {label} {el.required && <span className="text-red-600">*</span>}
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
          <input type="number" step="0.01" {...common} className="pl-8" placeholder={placeholder} />
        </div>
        {helpText && <div className="text-xs text-muted-foreground mt-1">{helpText}</div>}
      </div>
    );
  }
  if (el.type === 'date') {
    return (
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">
          {label} {el.required && <span className="text-red-600">*</span>}
        </label>
        <input type="date" {...common} />
        {helpText && <div className="text-xs text-muted-foreground mt-1">{helpText}</div>}
      </div>
    );
  }
  if (el.type === 'description') {
    return (
      <div className="text-sm text-foreground">
        {label && <div className="font-medium">{label}</div>}
        {helpText && <div className="text-muted-foreground text-xs mt-1">{helpText}</div>}
      </div>
    );
  }
  if (el.type === 'title') {
    return (
      <div className="mb-2">
        <h2 className="text-xl font-semibold">{label}</h2>
        {helpText && <div className="text-muted-foreground text-sm mt-1">{helpText}</div>}
      </div>
    );
  }
  if (el.type === 'single_choice') {
    const options = el.options || [];
    return (
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          {label} {el.required && <span className="text-red-600">*</span>}
        </label>
        {options.length === 0 ? (
          <div className="text-sm text-muted-foreground italic py-2 border border-dashed border-border rounded">
            No options configured. Add options in the field properties.
          </div>
        ) : (
          <div className="space-y-2">
            {options.map((opt, idx) => {
              const optLabel = typeof opt.label === 'object' ? (opt.label[locale] || opt.label.en || opt.value) : (opt.label || opt.value);
              const optValue = opt.value || optLabel;
              return (
                <label key={idx} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name={el.key}
                    value={optValue}
                    checked={value === optValue}
                    onChange={() => onChange(optValue)}
                    className="w-4 h-4 text-blue-600 border-input focus:ring-ring"
                  />
                  <span className="text-sm text-foreground">{optLabel}</span>
                </label>
              );
            })}
          </div>
        )}
        {helpText && <div className="text-xs text-muted-foreground mt-1">{helpText}</div>}
      </div>
    );
  }
  if (el.type === 'multi_select') {
    const options = el.options || [];
    const selectedValues = Array.isArray(value) ? value : [];
    return (
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          {label} {el.required && <span className="text-red-600">*</span>}
        </label>
        <div className="space-y-2">
          {options.map((opt, idx) => {
            const optLabel = typeof opt.label === 'object' ? (opt.label[locale] || opt.label.en || opt.value) : (opt.label || opt.value);
            return (
              <label key={idx} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedValues.includes(opt.value)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onChange([...selectedValues, opt.value]);
                    } else {
                      onChange(selectedValues.filter(v => v !== opt.value));
                    }
                  }}
                  className="w-4 h-4 text-blue-600 border-input rounded focus:ring-ring"
                />
                <span className="text-sm text-foreground">{optLabel}</span>
              </label>
            );
          })}
        </div>
        {helpText && <div className="text-xs text-muted-foreground mt-1">{helpText}</div>}
      </div>
    );
  }
  if (el.type === 'page_break') {
    // Page break is a structural element - in preview, show as a visual divider or nothing
    return (
      <div className="my-6 border-t border-border">
        <div className="text-center mt-4">
          <span className="text-xs text-muted-foreground bg-card px-2">Page Break</span>
        </div>
      </div>
    );
  }
  return <div className="text-xs text-muted-foreground p-2 border border-dashed border-border rounded">Unsupported field: {el.type}</div>;
};

const FormPreview = ({ form, device = 'desktop' }) => {
  const { branding } = useBranding();
  const [answers, setAnswers] = useState({});
  const [pageIdx, setPageIdx] = useState(0);
  const locale = form?.localeDefault || 'en';

  const page = useMemo(() => form?.pages?.[pageIdx] || null, [form, pageIdx]);
  
  // Determine final theme values (agency vs custom)
  const useAgencyBranding = form?.theme?.useAgencyBranding !== false; // Default true
  const primaryColor = useAgencyBranding ? (branding?.primaryColor || form?.theme?.primary || '#3B82F6') : (form?.theme?.primary || '#3B82F6');
  const logoUrl = useAgencyBranding ? (branding?.agencyLogo || form?.theme?.logoUrl) : form?.theme?.logoUrl;
  const coverImageUrl = useAgencyBranding ? (branding?.agencyCoverImage || form?.theme?.coverImageUrl) : form?.theme?.coverImageUrl;

  const handleNext = () => {
    const lastPage = (form?.pages?.length || 1) - 1;
    if (pageIdx < lastPage) {
      setPageIdx(pageIdx + 1);
    }
  };

  const handlePrev = () => {
    if (pageIdx > 0) {
      setPageIdx(pageIdx - 1);
    }
  };

  if (!form) return <div className="p-6 text-muted-foreground">No form data</div>;

  const isMobile = device === 'mobile';
  const containerClasses = isMobile 
    ? "w-full p-4 bg-background"
    : "max-w-2xl mx-auto p-6";

  return (
    <div className={containerClasses} dir={form.theme?.rtl ? 'rtl' : 'ltr'} style={{ '--primary-color': primaryColor }}>
      {coverImageUrl && (
        <div className={cn("mb-4 h-32 bg-cover bg-center", isMobile ? "rounded-lg -mx-4 -mt-4" : "-mx-6 -mt-6 rounded-t-lg")} style={{ backgroundImage: `url(${coverImageUrl})` }} />
      )}
      {logoUrl && (
        <div className={cn("mb-4", isMobile && !coverImageUrl && "mt-2")}><img alt="logo" src={logoUrl} className="h-10" /></div>
      )}
      <h1 className="text-2xl font-semibold mb-2 text-foreground">{form.name}</h1>
      {form.settings?.progressBar && form.pages?.length > 1 && (
        <div className="mb-4">
          <div className="text-sm text-muted-foreground mb-2">Page {pageIdx + 1} of {form.pages?.length || 1}</div>
          <div className="w-full bg-muted rounded-full h-2">
            <div 
              className="h-2 rounded-full transition-all"
              style={{ width: `${((pageIdx + 1) / (form.pages?.length || 1)) * 100}%`, backgroundColor: primaryColor }}
            />
          </div>
        </div>
      )}
      
      {page && (
        <div className="space-y-4 mb-6">
          {page.elements?.map(el => (
            <Field 
              key={el.id} 
              el={el} 
              value={answers[el.key]} 
              onChange={(v) => setAnswers({ ...answers, [el.key]: v })}
              locale={locale}
            />
          ))}
        </div>
      )}

      {page && page.elements?.length === 0 && (
        <div className="text-center py-12 text-muted-foreground border border-dashed border-border rounded-lg">
          This page is empty. Add fields in the builder.
        </div>
      )}

      {form.pages && form.pages.length > 1 && (
        <div className="flex items-center gap-2 pt-4 border-t border-border">
          {pageIdx > 0 && (
            <button onClick={handlePrev} className="px-4 py-2 rounded-lg border border-border hover:bg-muted text-foreground">
              Previous
            </button>
          )}
          <div className="flex-1" />
          {pageIdx < form.pages.length - 1 ? (
            <button onClick={handleNext} className="px-4 py-2 rounded-lg text-white hover:opacity-90 transition-opacity" style={{ backgroundColor: primaryColor }}>
              Next
            </button>
          ) : (
            <button className="px-4 py-2 rounded-lg text-white hover:opacity-90 transition-opacity" style={{ backgroundColor: primaryColor }}>
              Submit
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FormPreview;

